/**
 * "A new version is available" (PORTAL-NEW-VERSION-BANNER, 2026-10-20).
 *
 * The portal is a single-page app: an open tab keeps running the code it
 * loaded until it reloads, so after a deploy it can call the new API with old
 * code — on staging a long Replay kept failing until a hard refresh. Each
 * build carries the commit it was built from (VITE_GIT_SHA, the same commit
 * the API reports on /api/v1/healthz). This checks the API every minute and
 * when the tab or the browser window comes back into view; when the commits differ it offers a
 * reload. It never reloads by itself, so nothing half-typed is lost.
 */
import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';

// Every minute, and on tab switch or window focus: a tab left in front
// after a deploy waited up to five minutes before, and switching from
// another app to the browser was not noticed at all (2026-10-21).
export const CHECK_EVERY_MS = 60 * 1000;

/** True when the API runs a different commit from the one this page was
 *  built from. Local builds carry no commit and never ask. */
export function isNewerBuild(built: string | undefined, served: unknown): boolean {
  if (!built || built === 'unknown') return false;
  return typeof served === 'string' && served !== '' && served !== built;
}

export function NewVersionBanner() {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const built = import.meta.env.VITE_GIT_SHA;
    if (!built || built === 'unknown') return;
    let stopped = false;
    const check = async () => {
      if (stopped || document.visibilityState === 'hidden') return;
      try {
        const base = import.meta.env.VITE_API_URL ?? '';
        const resp = await fetch(`${base}/api/v1/healthz`, { cache: 'no-store' });
        if (!resp.ok) return;
        const body = (await resp.json()) as { sha?: unknown };
        if (!stopped && isNewerBuild(built, body.sha)) setAvailable(true);
      } catch {
        /* offline or mid-deploy: try again next time */
      }
    };
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const onFocus = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    void check();
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return (
    <Snackbar
      open={available && !dismissed}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <>
            <Button color="inherit" size="small" onClick={() => setDismissed(true)}>
              Later
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          </>
        }
      >
        A new version of 10 Newtons is available.
      </Alert>
    </Snackbar>
  );
}
